import React from 'react';
import { useRecoilValue } from 'recoil';
import { blockExportMetadata, blockSettings, customizedBlock } from 'venn-state';
import { useSelectedMetrics } from '../../../studio-blocks/logic/useSelectedMetrics';
import {
  returnsGridParserHelper,
  type ReturnsGridProps,
} from '../../../studio-blocks/components/blocks/returns-grid/returnsGridParserHelper';
import { StyledErrorState } from '../../../studio-blocks/components/StyledErrorState';
import StyledEmptyState from '../../../studio-blocks/components/StyledEmptyState';
import { extractMetricReturnsFrequency } from 'venn-utils';
import type { ColDef } from 'ag-grid-community';
import {
  type ReturnsGridRows,
  useReturnsGridData,
} from '../../../studio-blocks/components/blocks/returns-grid/useReturnsGridData';
import { analysisGridDefaultColDefOverrides } from '../../../studio-blocks/components/grid/AnalysisGrid';
import ExportableGrid from '../../../studio-blocks/components/grid/ExportableGrid';
import { useAppPrintMode } from '../../../print';
import { DATA_TYPE_EXCEL_STYLES } from '../../../studio-blocks/logic/customBlockUtils';
import { useCommonGridProps } from '../../../studio-blocks/logic/columnUtils';
import { useIsBlockGridCompact } from '../../../studio-blocks/logic/useIsBlockGridCompact';
import { useReturnsGridColumnDefs } from '../../../studio-blocks/components/blocks/returns-grid/useReturnsGridColumnDefs';
import { useFilteredRequestsAndAnalyses } from '../../utils/hooks/use-filtered-requests';
import type { AnalysisProps } from '../../types';
import { compact } from 'lodash';

export const ReturnsGridContent = ({ id }: { id: string }) => {
  const blockSetting = useRecoilValue(blockSettings(id));
  const selectedBlock = useRecoilValue(customizedBlock(id));
  const selectedMetrics = useSelectedMetrics();
  const { requests, analyses } = useFilteredRequestsAndAnalyses(id);
  const exportMetaData = useRecoilValue(blockExportMetadata(id));
  const { inPrintModeOrReportLab } = useAppPrintMode();

  const returnsGridProps = returnsGridParserHelper({
    metrics: selectedMetrics,
    selectedBlock,
    analysesGroup: analyses,
  });

  if (returnsGridProps === 'NO_METRIC') {
    return (
      <StyledErrorState
        selectedRefId={id}
        header="Calendar returns are only available with 1 metric selected"
        message="Please select only 1 metric"
      />
    );
  }

  if (returnsGridProps === 'NO_ANALYSIS') {
    return (
      <StyledEmptyState
        selectedRefId={id}
        header="Unable to run analysis"
        message="Please check the block inputs and configuration"
      />
    );
  }

  // Backend may disable certain frequencies, and in those circumstances, we are not allowed to show the data to the client.
  const disabledFrequencies = compact([
    ...(analyses?.[0]?.[0]?.returnsGrids?.[0]?.disabledFrequencies ?? []),
    requests?.[0]?.frequency === 'QUARTERLY' ? 'MONTHLY' : null,
  ]);
  if (
    disabledFrequencies?.includes(
      extractMetricReturnsFrequency(blockSetting.relativeToBenchmark, returnsGridProps.selectedMetric),
    )
  ) {
    return (
      <StyledErrorState
        selectedRefId={id}
        header="Unable to run analysis."
        message="Try selecting a different data frequency to view analysis."
      />
    );
  }

  return (
    <Grid
      id={id}
      requests={requests}
      exportMetaData={exportMetaData}
      returnsGridProps={returnsGridProps}
      inPrintMode={inPrintModeOrReportLab}
      exportable={analyses?.[0]?.[0]?.exportable?.[0] ?? false}
    />
  );
};

const returnsGridBlockDefaultColDefExtras: ColDef<ReturnsGridRows> = {
  ...analysisGridDefaultColDefOverrides,
  // We shouldn't need to wrap text because we have pretty simple cell contents of just years and percentages.
  wrapText: false,
};

const Grid = ({
  id,
  requests,
  returnsGridProps,
  ...otherProps
}: Omit<AnalysisProps, 'analyses' | 'isReport'> & {
  exportable: boolean;
  returnsGridProps: ReturnsGridProps;
}) => {
  const commonGridProps = useCommonGridProps(returnsGridBlockDefaultColDefExtras);

  // Note: Pivoting row data was neither implemented or required by spec, but if it becomes needed, check out how other grids do it.
  const rowData = useReturnsGridData(returnsGridProps);
  const isCompact = useIsBlockGridCompact();
  const columnDefs = useReturnsGridColumnDefs(rowData, requests, returnsGridProps);

  return (
    <ExportableGrid
      {...otherProps}
      {...commonGridProps}
      selectedRefId={id}
      rowData={rowData}
      columnDefs={columnDefs}
      excelStyles={DATA_TYPE_EXCEL_STYLES}
      isCompact={isCompact}
      tooltipShowDelay={1200}
    />
  );
};
